// Cuestionario guiado · NO listado en el sitio público.
// Ruta accesible solo si se comparte explícitamente (WhatsApp/Instagram/ads).
// Captura lead con suficiente contexto para que NULED decida atención
// directa o redirección a integrador (Omniious para B2C residencial).
//
// noindex: true en el SEO para que no aparezca en Google.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { theme } from '../../styles/theme';
import { Seo } from '../../lib/seo';

type Answers = {
  perfil: 'final' | 'electricista' | 'ld_arquitecto' | 'integrador' | 'distribuidor' | '';
  obra: 'casa_propia' | 'casa_cliente' | 'depto' | 'oficina' | 'hotel_restaurante' | 'retail' | 'otro' | '';
  estado_obra: 'planos' | 'obra_negra' | 'remodelacion' | 'instalado' | '';
  necesidad: 'dimmer_simple' | 'control_app' | 'sensores' | 'persianas' | 'sistema_completo' | 'no_se' | '';
  escala: 'menos_10' | '10_30' | '30_60' | 'mas_60' | '';
  tiempo: 'urgente' | '1_3_meses' | '3_6_meses' | 'investigando' | '';
  nombre: string;
  email: string;
  tel: string;
  ciudad: string;
  notas: string;
};

const STEPS_TOTAL = 7;

export function LutronAsesoria() {
  const [step, setStep] = useState(1);
  const [a, setA] = useState<Answers>({
    perfil: '', obra: '', estado_obra: '', necesidad: '', escala: '', tiempo: '',
    nombre: '', email: '', tel: '', ciudad: '', notas: '',
  });
  const [sent, setSent] = useState(false);

  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA(prev => ({ ...prev, [k]: v }));
  const next = () => setStep(s => Math.min(STEPS_TOTAL, s + 1));
  const prev = () => setStep(s => Math.max(1, s - 1));

  const submit = () => {
    // Por ahora solo console.log + abrir WhatsApp con el resumen
    // En producción esto va a Supabase como deal con channel='whatsapp_inbound'
    // y notes con el contexto completo
    const summary = [
      `Asesoría guiada Lutron`,
      `Perfil: ${a.perfil}`,
      `Obra: ${a.obra} · ${a.estado_obra}`,
      `Necesidad: ${a.necesidad}`,
      `Escala: ${a.escala}`,
      `Tiempo: ${a.tiempo}`,
      `Nombre: ${a.nombre}`,
      `Email: ${a.email}`,
      `Tel: ${a.tel}`,
      `Ciudad: ${a.ciudad}`,
      a.notas ? `Notas: ${a.notas}` : '',
    ].filter(Boolean).join('\n');
    console.log('Lead capturado:', summary);
    setSent(true);
  };

  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans, minHeight: '100vh' }}>
      <Seo
        title="Asesoría Lutron · NULED"
        description="Cuestionario guiado para encontrar la solución Lutron correcta para tu proyecto."
        // No listada — robots noindex se maneja con meta tag custom abajo
      />

      {/* Meta robots noindex */}
      <NoIndexMeta />

      <Header />

      <section style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '40px 24px 80px',
      }}>
        {!sent ? (
          <>
            {/* Header del wizard */}
            <div style={{
              fontSize: 11,
              color: theme.accentDark,
              textTransform: 'uppercase',
              letterSpacing: 2,
              fontWeight: 700,
            }}>
              Asesoría guiada · Lutron
            </div>
            <h1 style={{
              fontFamily: theme.font.head,
              fontSize: 32,
              fontWeight: 400,
              color: theme.brand,
              margin: '8px 0 8px',
            }}>
              Te ayudamos a elegir
            </h1>
            <p style={{ color: theme.textMuted, fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              2 minutos. Te preguntamos lo justo para entender tu proyecto y
              recomendarte la línea Lutron correcta — Caséta, RadioRA 3,
              Wallbox, Maestro, Vive o HomeWorks. Tu información solo la usamos
              para contactarte.
            </p>

            {/* Progress bar */}
            <div style={{ marginTop: 32, marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: theme.textMuted, marginBottom: 6, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                <span>Paso {step} de {STEPS_TOTAL}</span>
                <span>{Math.round((step / STEPS_TOTAL) * 100)}%</span>
              </div>
              <div style={{ background: theme.bgSoft, height: 6 }}>
                <div style={{
                  background: theme.accent,
                  height: '100%',
                  width: `${(step / STEPS_TOTAL) * 100}%`,
                  transition: 'width 0.25s ease-out',
                }} />
              </div>
            </div>

            {/* Steps */}
            <div className="fade-in" key={step}>
              {step === 1 && (
                <Step
                  title="¿Cómo te identificas?"
                  caption="Esto nos ayuda a darte la atención correcta."
                  options={[
                    { value: 'final',          label: 'Cliente final · es para mi casa o negocio' },
                    { value: 'electricista',   label: 'Soy electricista' },
                    { value: 'ld_arquitecto',  label: 'Soy lighting designer / arquitecto' },
                    { value: 'integrador',     label: 'Soy integrador (smart home, AV)' },
                    { value: 'distribuidor',   label: 'Soy distribuidor / quiero serlo' },
                  ]}
                  value={a.perfil}
                  onPick={v => { set('perfil', v as Answers['perfil']); next(); }}
                />
              )}
              {step === 2 && (
                <Step
                  title="¿Qué tipo de obra?"
                  options={[
                    { value: 'casa_propia',          label: 'Mi casa o departamento propio' },
                    { value: 'casa_cliente',         label: 'Casa de un cliente' },
                    { value: 'depto',                label: 'Departamento / desarrollo residencial' },
                    { value: 'oficina',              label: 'Oficina / coworking' },
                    { value: 'hotel_restaurante',    label: 'Hotel / restaurante' },
                    { value: 'retail',               label: 'Retail / tienda' },
                    { value: 'otro',                 label: 'Otro' },
                  ]}
                  value={a.obra}
                  onPick={v => { set('obra', v as Answers['obra']); next(); }}
                />
              )}
              {step === 3 && (
                <Step
                  title="¿En qué etapa está la obra?"
                  options={[
                    { value: 'planos',         label: 'En planos · estamos especificando' },
                    { value: 'obra_negra',     label: 'Obra negra · cableado en proceso' },
                    { value: 'remodelacion',   label: 'Remodelación · paredes ya levantadas' },
                    { value: 'instalado',      label: 'Ya instalado · cambio de luminarias o switches' },
                  ]}
                  value={a.estado_obra}
                  onPick={v => { set('estado_obra', v as Answers['estado_obra']); next(); }}
                />
              )}
              {step === 4 && (
                <Step
                  title="¿Qué necesitas controlar?"
                  options={[
                    { value: 'dimmer_simple',     label: 'Atenuar luces con dimmer de pared (simple)' },
                    { value: 'control_app',       label: 'Controlar luces desde app o voz (Alexa, HomeKit)' },
                    { value: 'sensores',          label: 'Sensores de ocupación / luz natural (ahorro)' },
                    { value: 'persianas',         label: 'Persianas motorizadas / shading' },
                    { value: 'sistema_completo',  label: 'Sistema completo · keypads, escenas, multi-zona' },
                    { value: 'no_se',             label: 'No estoy seguro · ayúdame a decidir' },
                  ]}
                  value={a.necesidad}
                  onPick={v => { set('necesidad', v as Answers['necesidad']); next(); }}
                />
              )}
              {step === 5 && (
                <Step
                  title="¿Qué escala tiene el proyecto?"
                  caption="Cuántos puntos de luz / dimmers aproximadamente."
                  options={[
                    { value: 'menos_10',   label: 'Menos de 10 dispositivos' },
                    { value: '10_30',      label: '10 a 30 dispositivos' },
                    { value: '30_60',      label: '30 a 60 dispositivos' },
                    { value: 'mas_60',     label: 'Más de 60 dispositivos' },
                  ]}
                  value={a.escala}
                  onPick={v => { set('escala', v as Answers['escala']); next(); }}
                />
              )}
              {step === 6 && (
                <Step
                  title="¿Cuándo necesitas que esté instalado?"
                  options={[
                    { value: 'urgente',       label: 'Urgente · este mes' },
                    { value: '1_3_meses',     label: '1 a 3 meses' },
                    { value: '3_6_meses',     label: '3 a 6 meses' },
                    { value: 'investigando',  label: 'Solo estoy investigando · sin prisa' },
                  ]}
                  value={a.tiempo}
                  onPick={v => { set('tiempo', v as Answers['tiempo']); next(); }}
                />
              )}
              {step === 7 && (
                <div>
                  <h2 style={stepTitle}>Casi listo · ¿Cómo te contactamos?</h2>
                  <p style={stepCaption}>
                    Te llamamos o escribimos en menos de 24 horas con una recomendación
                    personalizada. Nadie pasa tu información a terceros.
                  </p>
                  <div style={{ display: 'grid', gap: 16, marginTop: 24 }}>
                    <Field label="Nombre completo *">
                      <input
                        value={a.nombre}
                        onChange={e => set('nombre', e.target.value)}
                        style={inputStyle}
                        placeholder="Ej. María Hernández"
                        required
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        type="email"
                        value={a.email}
                        onChange={e => set('email', e.target.value)}
                        style={inputStyle}
                        placeholder="tu@email.com"
                        required
                      />
                    </Field>
                    <Field label="Teléfono / WhatsApp *">
                      <input
                        type="tel"
                        value={a.tel}
                        onChange={e => set('tel', e.target.value)}
                        style={inputStyle}
                        placeholder="55 1234 5678"
                        required
                      />
                    </Field>
                    <Field label="Ciudad / Estado">
                      <input
                        value={a.ciudad}
                        onChange={e => set('ciudad', e.target.value)}
                        style={inputStyle}
                        placeholder="CDMX, Monterrey, Guadalajara…"
                      />
                    </Field>
                    <Field label="Algo más que quieras contarnos (opcional)">
                      <textarea
                        value={a.notas}
                        onChange={e => set('notas', e.target.value)}
                        style={{ ...inputStyle, minHeight: 80, fontFamily: theme.font.sans }}
                        placeholder="Comentarios, urgencias, marcas que ya tienes consideradas…"
                      />
                    </Field>
                  </div>

                  <button
                    onClick={submit}
                    disabled={!a.nombre || !a.email || !a.tel}
                    style={{
                      marginTop: 24,
                      width: '100%',
                      background: theme.accent,
                      color: theme.brand,
                      border: 'none',
                      padding: '16px 28px',
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      cursor: a.nombre && a.email && a.tel ? 'pointer' : 'not-allowed',
                      opacity: a.nombre && a.email && a.tel ? 1 : 0.5,
                      fontFamily: theme.font.sans,
                    }}
                  >
                    Enviar mi asesoría
                  </button>
                  <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8, textAlign: 'center' }}>
                    Al enviar aceptas nuestra <Link to="/aviso-privacidad" style={{ color: theme.accentDark }}>política de privacidad</Link>.
                  </div>
                </div>
              )}
            </div>

            {/* Nav */}
            {step > 1 && step < 7 && (
              <button
                onClick={prev}
                style={{
                  marginTop: 24,
                  background: 'transparent',
                  border: 'none',
                  color: theme.textMuted,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: theme.font.sans,
                  textDecoration: 'underline',
                }}
              >
                ← Paso anterior
              </button>
            )}
            {step === 7 && (
              <button
                onClick={prev}
                style={{
                  marginTop: 16,
                  background: 'transparent',
                  border: 'none',
                  color: theme.textMuted,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: theme.font.sans,
                  textDecoration: 'underline',
                }}
              >
                ← Editar respuestas
              </button>
            )}
          </>
        ) : (
          /* Confirmación */
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: theme.accent,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              color: theme.brand,
              marginBottom: 24,
            }}>
              ✓
            </div>
            <h1 style={{
              fontFamily: theme.font.head,
              fontSize: 32,
              fontWeight: 400,
              color: theme.brand,
              margin: '0 0 12px',
            }}>
              Recibido. Gracias, {a.nombre.split(' ')[0]}.
            </h1>
            <p style={{ color: theme.textMuted, fontSize: 16, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
              Un especialista de NULED te contactará en menos de 24 horas
              con la recomendación correcta para tu proyecto. Si es urgente,
              también puedes escribirnos directo por WhatsApp.
            </p>
            <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={`https://wa.me/525549332582?text=${encodeURIComponent(`Hola NULED, acabo de llenar la asesoría guiada (${a.nombre}). Quiero adelantar.`)}`}
                style={{
                  background: theme.brand,
                  color: theme.textOnBrand,
                  padding: '14px 28px',
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Adelantar por WhatsApp
              </a>
              <Link to="/" style={{
                border: `1px solid ${theme.border}`,
                color: theme.brand,
                padding: '14px 28px',
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}>
                Volver al inicio
              </Link>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

// ===================== sub-componentes =====================

function Step({ title, caption, options, value, onPick }: {
  title: string;
  caption?: string;
  options: { value: string; label: string }[];
  value: string;
  onPick: (v: string) => void;
}) {
  return (
    <div>
      <h2 style={stepTitle}>{title}</h2>
      {caption && <p style={stepCaption}>{caption}</p>}
      <div style={{ display: 'grid', gap: 8, marginTop: 20 }}>
        {options.map(opt => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onPick(opt.value)}
              style={{
                textAlign: 'left',
                padding: '16px 18px',
                background: active ? theme.bgFooter : theme.bg,
                border: `1px solid ${active ? theme.accent : theme.border}`,
                borderLeft: `4px solid ${active ? theme.accent : theme.border}`,
                color: theme.brand,
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                fontFamily: theme.font.sans,
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.borderLeftColor = theme.accentDark; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.borderLeftColor = theme.border; }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11,
        color: theme.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: 700,
        marginBottom: 6,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function NoIndexMeta() {
  // Inserta meta robots noindex,nofollow para que esta página no aparezca en Google
  if (typeof document !== 'undefined') {
    let m = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!m) {
      m = document.createElement('meta');
      m.name = 'robots';
      document.head.appendChild(m);
    }
    m.content = 'noindex, nofollow';
  }
  return null;
}

const stepTitle: React.CSSProperties = {
  fontFamily: theme.font.head,
  fontSize: 24,
  fontWeight: 500,
  color: theme.brand,
  margin: '0 0 8px',
  lineHeight: 1.2,
};

const stepCaption: React.CSSProperties = {
  fontSize: 14,
  color: theme.textMuted,
  margin: 0,
  lineHeight: 1.55,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '12px 14px',
  fontSize: 14,
  color: theme.text,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};
